import type { InspectionFormState } from "../useInspectionForm";
import type { ArborInspectionDetail } from "@/types/arboricultural-inspection";
import type { InspectionReadinessApi } from "@/lib/api";

interface Props {
  state: InspectionFormState;
  evidence: ArborInspectionDetail["evidence"];
  treeData?: { ctaCode?: string; species?: string; scientificName?: string; address?: string } | null;
  /** Pendências lidas do servidor: a mesma regra que ele aplica ao enviar. */
  readiness?: InspectionReadinessApi | null;
}

function Section({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-background-200/70 p-4">
      <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2 mb-3">
        <i className={`${icon} text-primary-500`}></i>
        {title}
      </h4>
      {children}
    </div>
  );
}

const F_BY_STATUS: Record<string, string> = {
  BOM: "F1 — Boa / vigorosa",
  REGULAR: "F2 — Regular",
  RUIM: "F3 — Ruim / declínio",
  CRITICO: "F4 — Morte iminente",
  MORTO: "F4 — Morta",
};
const fLabel = (status?: string | null) => (status ? (F_BY_STATUS[status] ?? status) : null);

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <span className="text-[10px] uppercase tracking-wider text-foreground-400 font-semibold">{label}</span>
      <p className="text-sm text-foreground-700">{value || <span className="text-foreground-300">—</span>}</p>
    </div>
  );
}

export default function StepRevisao({ state, evidence, treeData, readiness }: Props) {
  const ha = state.healthAssessment;
  const ra = state.riskAssessment;
  const comp = state.compensation;

  const blocking = readiness?.submission.filter((i) => i.blocking) ?? [];
  const advisory = readiness?.submission.filter((i) => !i.blocking) ?? [];

  return (
    <div className="space-y-5">
      {readiness == null && (
        <div className="bg-background-50 border border-background-200 rounded-xl p-3 text-xs text-foreground-500">
          Verificando as exigências do manual com o servidor...
        </div>
      )}

      {blocking.length > 0 && (
        <div className="bg-danger-50 border border-danger-200 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-danger-700 flex items-center gap-2 mb-2">
            <i className="ri-close-circle-line"></i>
            Impedem o envio
          </h4>
          <ul className="list-disc list-inside text-xs text-danger-700 space-y-1">
            {blocking.map((i) => (
              <li key={i.code}>{i.message}</li>
            ))}
          </ul>
        </div>
      )}

      {advisory.length > 0 && (
        <div className="bg-warning-50 border border-warning-200 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-warning-700 flex items-center gap-2 mb-2">
            <i className="ri-error-warning-line"></i>
            Recomendações (não bloqueiam)
          </h4>
          <ul className="list-disc list-inside text-xs text-warning-700 space-y-1">
            {advisory.map((i) => (
              <li key={i.code}>{i.message}</li>
            ))}
          </ul>
        </div>
      )}

      {readiness != null && blocking.length === 0 && advisory.length === 0 && (
        <div className="bg-primary-50 border border-primary-200 rounded-xl p-3 text-xs text-primary-800 flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-base"></i>
          Nada pendente: a vistoria atende às exigências do manual para ser enviada.
        </div>
      )}

      <Section title="Identificação" icon="ri-tree-line">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="Código da árvore" value={treeData?.ctaCode} />
          <Field label="Espécie" value={treeData?.species} />
          <Field label="Endereço" value={treeData?.address} />
          <Field label="Data da inspeção" value={state.inspectionDate} />
          <Field label="Identificação botânica" value={state.botanicalIdentificationStatus} />
          <Field label="Localização confirmada" value={state.locationConfirmed ? "Sim" : "Não"} />
        </div>
      </Section>

      <Section title="Dendrometria" icon="ri-ruler-line">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="CAP (cm)" value={state.measurement.capCm} />
          <Field label="DAP (cm)" value={state.measurement.dapCm?.toFixed(2)} />
          <Field label="Método DAP" value={state.measurement.measurementSource} />
          <Field label="Altura (m)" value={state.measurement.heightM} />
          <Field label="Copa (m)" value={state.measurement.crownDiameterM} />
          <Field label="Fustes adicionais" value={state.stems.length} />
        </div>
      </Section>

      <Section title="Fitossanidade" icon="ri-heart-pulse-line">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="Condição (F1–F4)" value={fLabel(ha.generalStatus)} />
          <Field label="Raízes (R1–R4)" value={ha.rootCondition} />
          <Field label="Achados fitossanitários" value={state.healthFindings.length} />
          <Field label="Achados biomecânicos" value={state.mechanicalFindings.length} />
        </div>
      </Section>

      <Section title="Risco" icon="ri-alert-line">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="Classe de risco" value={ra.riskClass} />
          <Field label="Score" value={ra.riskScore} />
          <Field label="Prioridade (P0–P4)" value={ra.priorityClass} />
          <Field label="Exposição de alvos (A0–A2)" value={ra.targetClass} />
          <Field label="Alvos detalhados" value={state.riskTargets.length} />
        </div>
      </Section>

      <Section title="Conflitos" icon="ri-flashlight-line">
        <Field label="Conflitos urbanos registrados" value={state.interferences.length} />
      </Section>

      <Section title="Parecer" icon="ri-stethoscope-line">
        <div className="space-y-2">
          <Field label="Diagnóstico" value={state.diagnosis} />
          <Field label="Parecer técnico" value={state.technicalOpinion} />
          <Field label="Recomendações" value={state.recommendations.length} />
          <Field label="Compensação indicada" value={comp.compensationIndicated ? "Sim" : "Não"} />
          {comp.compensationIndicated && (
            <Field label="Mudas propostas" value={comp.proposedSeedlingQuantity} />
          )}
          <Field label="Requer monitoramento" value={state.requiresMonitoring ? "Sim" : "Não"} />
        </div>
      </Section>

      <Section title="Evidências" icon="ri-camera-line">
        <Field label="Total de evidências" value={evidence.length} />
      </Section>

      <Section title="Responsabilidade Técnica" icon="ri-user-star-line">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Inspetor" value={state.inspectorName} />
          <Field label="Conselho" value={state.professionalCouncil} />
          <Field label="Registro" value={state.professionalRegistration} />
        </div>
      </Section>
    </div>
  );
}
